import {useCallback, useEffect, useState} from "react";
import {useLocation} from "react-router";
import {css, Theme} from "@emotion/react";

import {borderRadius} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {appendQueryString} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";

import {rpCommonUrl} from "../../../../app/read_rp_environment_variables";
import {openLeadModal} from "../../../../application/state/main_lead_slice";
import {ApplicationSource, ApplicationSourceSection} from "../../../../application/utils/form/application_form_source";
import {addWarningNotification} from "../../../../notifications/utils/add_warning_notification";
import {useGetPropertyListQuery} from "../../../../property/api/get_property_list";
import {propertyListConstraints} from "../../../../property/constants/property_list_constraints";
import {useAppDispatch} from "../../../../store/hooks";
import {compareJeffProperties, getPlanner3dData} from "../../../constants/get_planner_3d_data";
import {YslabProperty} from "./api/yslab_investments_api";
import {YslabEvents, YslabEventTypes, YslabJeffAppEventTypes} from "./types/YslabEventTypes";
import {getYslabPropertyUrl} from "./get_yslab_property_url";
import {debouncedTrackYslabEvent} from "./track_yslab_event";

interface Props {
    closeModal: (forceClose?: boolean) => void;
    modalState: boolean;
    vendorId: number;
    offerId: number;
    offerName: string;
    modalId: string;
    yslabProperty?: YslabProperty | null;
}

const allEvents = [...Object.values(YslabEventTypes), ...Object.values(YslabJeffAppEventTypes)];

export const YslabPlanner3dModal = (props: Props) => {
    const dispatch = useAppDispatch();

    const showError = (content?: string) => {
        dispatch(addWarningNotification(content || "Przepraszamy, wystąpił błąd podczas ładowania Planu 3D"));
    };

    const onIframeError = () => {
        showError();
        props.closeModal();
    };

    const plannerData = getPlanner3dData(props.offerId);

    const yslabAppBaseUrl = plannerData?.plannerUrl && appendQueryString(plannerData.plannerUrl, plannerData.defaultOptions ? plannerData.defaultOptions : {});
    const yslabPropertyViewUrl = props.yslabProperty && getYslabPropertyUrl(plannerData, props.yslabProperty);
    const iframeSrc = yslabPropertyViewUrl || yslabAppBaseUrl;

    const location = useLocation();

    const {data: propertyListData, error} = useGetPropertyListQuery(
        {offer: props.offerId, page: 1, page_size: 1000, ...propertyListConstraints},
        {skip: !props.modalState}
    );

    const [isIframeLoading, setIsIframeLoading] = useState(true);

    const openLead = (selectedPropertyId: number | null, applicationText?: string) => {
        dispatch(
            openLeadModal({
                vendorId: props.vendorId,
                offerId: props.offerId,
                propertyId: selectedPropertyId || null,
                source: selectedPropertyId ? ApplicationSource.Property : ApplicationSource.Offer,
                sourceSection: ApplicationSourceSection.MODAL,
                hideQuestions: true,
                customFormText: applicationText,
                trackingMeta: {
                    openButtonSourceText: "N/A - Planner3DYslabModal",
                    openButtonSource: ""
                }
            })
        );
    };

    const openApplicationModal = (properties: string[]) => {
        let applicationText = undefined;
        let selectedPropertyId = undefined;

        if (!error && properties) {
            const foundProperties = properties
                .map((property) => propertyListData?.results.find((p) => normalizeNumber(property) === normalizeNumber(p.number)))
                .filter(Boolean);

            if (foundProperties.length === 1) {
                selectedPropertyId = foundProperties[0]?.id;
            }

            if (foundProperties.length === 0 || foundProperties.length > 1) {
                selectedPropertyId = undefined;
                applicationText = `Zainteresowały mnie nieruchomości: ${properties.join(", ")}. Proszę o kontakt.`;
            }
        }

        openLead(selectedPropertyId || null, applicationText);
    };

    const openApplicationModalOnError = (text: string) => {
        openLead(null, text);
    };

    const handleEvent = useCallback(
        (event: YslabEvents) => {
            const isYslabEvent = allEvents.some((name) => name === event.data?.type);

            if (isYslabEvent) {
                debouncedTrackYslabEvent(event);

                switch (event.data.type) {
                    case YslabEventTypes.APP_OPEN_FORM:
                        openApplicationModal(event.data.apartmentsNames);
                        break;
                    case YslabEventTypes.APP_CLOSE_MOBILE:
                        props.closeModal();
                        break;
                    case YslabEventTypes.APP_COPY_URL:
                        if (event.data.apartment) {
                            // casting because TS went dumb and does not recognize event type
                            const apartmentData = event.data as {apartment: {name: string}};
                            const property = propertyListData?.results.find((p) => p.number === apartmentData.apartment.name);

                            if (property) {
                                const propertyLink = rpAppLink.property.detail.base({
                                    vendorSlug: property.offer.vendor.slug,
                                    offerSlug: property.offer.slug,
                                    offerId: property.offer.id,
                                    propertyId: property.id
                                });

                                navigator.clipboard.writeText(rpCommonUrl + propertyLink);
                                return;
                            }
                        }

                        navigator.clipboard.writeText(rpCommonUrl + location.pathname);
                        break;
                    case YslabJeffAppEventTypes.APP_APARTMENT_SELECTED:
                        if (event.data.apartment.status === "available" || event.data.apartment.status === "reserved") {
                            const property = propertyListData?.results.find(
                                compareJeffProperties(
                                    event.data.apartment,
                                    plannerData.allowComparisonBasedOnConfiguredBuildingName,
                                    plannerData.buildingNameMap
                                )
                            );
                            if (property) {
                                const propertyLink = rpAppLink.property.detail.base({
                                    vendorSlug: property.offer.vendor.slug,
                                    offerSlug: property.offer.slug,
                                    offerId: property.offer.id,
                                    propertyId: property.id
                                });
                                window.open(rpCommonUrl + propertyLink, "_blank");
                                return;
                            }
                        }

                        openApplicationModalOnError(
                            `Użytkownik jest zainteresowany ${event.data.apartment.rooms}-pokojowym mieszkaniem w inwestycji ${props.offerName}`
                        );

                        break;
                    case YslabEventTypes.APP_ERROR:
                        if (props.modalState) {
                            showError();
                            props.closeModal(true);
                        }
                }
            }
        },
        [propertyListData]
    );

    useEffect(() => {
        if (props.modalState) {
            window.addEventListener("message", handleEvent);
        }
        return () => {
            window.removeEventListener("message", handleEvent);
        };
    }, [props.modalState, propertyListData, handleEvent]);

    return !plannerData?.plannerUrl ? null : (
        <Modal isOpen={props.modalState} shouldCloseOnOverlayClick showCloseButton css={modalStyle} onModalClose={() => props.closeModal(true)}>
            <Modal.CloseButton placement="right" wrapperColor="#fff" size="1.6" css={closeButtonStyle} />
            {isIframeLoading && (
                <div css={loaderWrapper}>
                    <Loader css={loaderPosition} />
                </div>
            )}
            <iframe src={iframeSrc} css={iframeStyle} onError={onIframeError} onLoad={() => setIsIframeLoading(false)} />
        </Modal>
    );
};

const normalizeNumber = (number: string) => {
    return number.replace(/[^a-z0-9]/gi, "").toLowerCase();
};

const modalStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.md}) {
        aspect-ratio: 16/9;
        width: 90vw;
        max-width: 1600px;
        max-height: 1200px;
    }
`;
const iframeStyle = css`
    width: 100%;
    height: 100%;
    border: none;
`;

const loaderPosition = css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const loaderWrapper = css`
    width: 100%;
    height: 100%;
    position: absolute;
    background-color: #fff;
`;

const closeButtonStyle = (theme: Theme) => css`
    // use yslab app native navigation on mobile
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        display: block;
        ${borderRadius(0, 0, 0, 2)};
    }
`;
import {useCallback, useEffect, useState} from "react";
import {useLocation} from "react-router";
import {css, Theme} from "@emotion/react";

import {borderRadius} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {appendQueryString} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";

import {rpCommonUrl} from "../../../../app/read_rp_environment_variables";
import {openLeadModal} from "../../../../application/state/main_lead_slice";
import {ApplicationSource, ApplicationSourceSection} from "../../../../application/utils/form/application_form_source";
import {addWarningNotification} from "../../../../notifications/utils/add_warning_notification";
import {useGetPropertyListQuery} from "../../../../property/api/get_property_list";
import {propertyListConstraints} from "../../../../property/constants/property_list_constraints";
import {useAppDispatch} from "../../../../store/hooks";
import {compareJeffProperties, getPlanner3dData} from "../../../constants/get_planner_3d_data";
import {YslabProperty} from "./api/yslab_investments_api";
import {YslabEvents, YslabEventTypes, YslabJeffAppEventTypes} from "./types/YslabEventTypes";
import {getYslabPropertyUrl} from "./get_yslab_property_url";
import {debouncedTrackYslabEvent} from "./track_yslab_event";

interface Props {
    closeModal: (forceClose?: boolean) => void;
    modalState: boolean;
    vendorId: number;
    offerId: number;
    offerName: string;
    modalId: string;
    yslabProperty?: YslabProperty | null;
}

const allEvents = [...Object.values(YslabEventTypes), ...Object.values(YslabJeffAppEventTypes)];

export const YslabPlanner3dModal = (props: Props) => {
    const dispatch = useAppDispatch();

    const showError = (content?: string) => {
        dispatch(addWarningNotification(content || "Przepraszamy, wystąpił błąd podczas ładowania Planu 3D"));
    };

    const onIframeError = () => {
        showError();
        props.closeModal();
    };

    const plannerData = getPlanner3dData(props.offerId);

    const yslabAppBaseUrl = plannerData?.plannerUrl && appendQueryString(plannerData.plannerUrl, plannerData.defaultOptions ? plannerData.defaultOptions : {});
    const yslabPropertyViewUrl = props.yslabProperty && getYslabPropertyUrl(plannerData, props.yslabProperty);
    const iframeSrc = yslabPropertyViewUrl || yslabAppBaseUrl;

    const location = useLocation();

    const {data: propertyListData, error} = useGetPropertyListQuery(
        {offer: props.offerId, page: 1, page_size: 1000, ...propertyListConstraints},
        {skip: !props.modalState}
    );

    const [isIframeLoading, setIsIframeLoading] = useState(true);

    const openLead = (selectedPropertyId: number | null, applicationText?: string) => {
        dispatch(
            openLeadModal({
                vendorId: props.vendorId,
                offerId: props.offerId,
                propertyId: selectedPropertyId || null,
                source: selectedPropertyId ? ApplicationSource.Property : ApplicationSource.Offer,
                sourceSection: ApplicationSourceSection.MODAL,
                hideQuestions: true,
                customFormText: applicationText,
                trackingMeta: {
                    openButtonSourceText: "N/A - Planner3DYslabModal",
                    openButtonSource: ""
                }
            })
        );
    };

    const openApplicationModal = (properties: string[]) => {
        let applicationText = undefined;
        let selectedPropertyId = undefined;

        if (!error && properties) {
            const foundProperties = properties
                .map((property) => propertyListData?.results.find((p) => normalizeNumber(property) === normalizeNumber(p.number)))
                .filter(Boolean);

            if (foundProperties.length === 1) {
                selectedPropertyId = foundProperties[0]?.id;
            }

            if (foundProperties.length === 0 || foundProperties.length > 1) {
                selectedPropertyId = undefined;
                applicationText = `Zainteresowały mnie nieruchomości: ${properties.join(", ")}. Proszę o kontakt.`;
            }
        }

        openLead(selectedPropertyId || null, applicationText);
    };

    const openApplicationModalOnError = (text: string) => {
        openLead(null, text);
    };

    const handleEvent = useCallback(
        (event: YslabEvents) => {
            const isYslabEvent = allEvents.some((name) => name === event.data?.type);

            if (isYslabEvent) {
                debouncedTrackYslabEvent(event);

                switch (event.data.type) {
                    case YslabEventTypes.APP_OPEN_FORM:
                        openApplicationModal(event.data.apartmentsNames);
                        break;
                    case YslabEventTypes.APP_CLOSE_MOBILE:
                        props.closeModal();
                        break;
                    case YslabEventTypes.APP_COPY_URL:
                        if (event.data.apartment) {
                            // casting because TS went dumb and does not recognize event type
                            const apartmentData = event.data as {apartment: {name: string}};
                            const property = propertyListData?.results.find((p) => p.number === apartmentData.apartment.name);

                            if (property) {
                                const propertyLink = rpAppLink.property.detail.base({
                                    vendorSlug: property.offer.vendor.slug,
                                    offerSlug: property.offer.slug,
                                    offerId: property.offer.id,
                                    propertyId: property.id
                                });

                                navigator.clipboard.writeText(rpCommonUrl + propertyLink);
                                return;
                            }
                        }

                        navigator.clipboard.writeText(rpCommonUrl + location.pathname);
                        break;
                    case YslabJeffAppEventTypes.APP_APARTMENT_SELECTED:
                        if (event.data.apartment.status === "available" || event.data.apartment.status === "reserved") {
                            const property = propertyListData?.results.find(
                                compareJeffProperties(
                                    event.data.apartment,
                                    plannerData.allowComparisonBasedOnConfiguredBuildingName,
                                    plannerData.buildingNameMap
                                )
                            );
                            if (property) {
                                const propertyLink = rpAppLink.property.detail.base({
                                    vendorSlug: property.offer.vendor.slug,
                                    offerSlug: property.offer.slug,
                                    offerId: property.offer.id,
                                    propertyId: property.id
                                });
                                window.open(rpCommonUrl + propertyLink, "_blank");
                                return;
                            }
                        }

                        openApplicationModalOnError(
                            `Użytkownik jest zainteresowany ${event.data.apartment.rooms}-pokojowym mieszkaniem w inwestycji ${props.offerName}`
                        );

                        break;
                    case YslabEventTypes.APP_ERROR:
                        if (props.modalState) {
                            showError();
                            props.closeModal(true);
                        }
                }
            }
        },
        [propertyListData]
    );

    useEffect(() => {
        if (props.modalState) {
            window.addEventListener("message", handleEvent);
        }
        return () => {
            window.removeEventListener("message", handleEvent);
        };
    }, [props.modalState, propertyListData, handleEvent]);

    return !plannerData?.plannerUrl ? null : (
        <Modal isOpen={props.modalState} shouldCloseOnOverlayClick showCloseButton css={modalStyle} onModalClose={() => props.closeModal(true)}>
            <Modal.CloseButton placement="right" wrapperColor="#fff" size="1.6" css={closeButtonStyle} />
            {isIframeLoading && (
                <div css={loaderWrapper}>
                    <Loader css={loaderPosition} />
                </div>
            )}
            <iframe src={iframeSrc} css={iframeStyle} onError={onIframeError} onLoad={() => setIsIframeLoading(false)} />
        </Modal>
    );
};

const normalizeNumber = (number: string) => {
    return number.replace(/[^a-z0-9]/gi, "").toLowerCase();
};

const modalStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.md}) {
        aspect-ratio: 16/9;
        width: 90vw;
        max-width: 1600px;
        max-height: 1200px;
    }
`;
const iframeStyle = css`
    width: 100%;
    height: 100%;
    border: none;
`;

const loaderPosition = css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const loaderWrapper = css`
    width: 100%;
    height: 100%;
    position: absolute;
    background-color: #fff;
`;

const closeButtonStyle = (theme: Theme) => css`
    // use yslab app native navigation on mobile
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        display: block;
        ${borderRadius(0, 0, 0, 2)};
    }
`;
